import { useCallback, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";

import Alert from "components/Alert";
import Button from "components/Button";
import ButtonBase from "components/ButtonBase";
import { LinearProgress } from "components/Loading";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import { ISuperuserDeal, useSuperuserDeals } from "entities/Superuser/sdk";

export interface IDealSelect {
  onStepAdvance: ({ deal }: { deal: ISuperuserDeal }) => void;
}

const DealSelect = ({ onStepAdvance }: IDealSelect) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDeal, setSelectedDeal] = useState<ISuperuserDeal | null>(null);

  const handleTextInputChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const { data: deals, isValidating: isLoadingDeals } = useSuperuserDeals({
    searchTerm,
    enabled: searchTerm.length > 2,
  });

  return (
    <Stack
      flexGrow={1}
      gap={1}
      marginTop={1}
      sx={{
        height: "calc(100vh - 245px)",
      }}
    >
      <Alert severity="info">
        Select a deal you'd like to import files into.
      </Alert>

      <TextField
        autoFocus
        size="small"
        defaultValue={searchTerm}
        onChange={_.debounce(handleTextInputChange, 300)}
        placeholder="Enter 3 or more letters to search for a deal"
      />

      <Stack
        sx={{
          borderRadius: "4px",
          overflowY: "auto",
          flexGrow: 1,
          border: `1px solid ${colors.gray40}`,
        }}
      >
        {searchTerm.length < 3 && (
          <Text padding={2}>
            Please enter at least 3 letters to search for a deal.
          </Text>
        )}

        {isLoadingDeals && (
          <LinearProgress sx={{ position: "sticky", left: 0 }} />
        )}

        {deals?.map((deal) => (
          <ButtonBase
            key={deal.id}
            onClick={() => setSelectedDeal(deal)}
            sx={{
              padding: 1,
              transition: "backgroundColor 0.2s ease",
              backgroundColor:
                selectedDeal?.id === deal.id ? colors.blue20 : "transparent",
              alignItems: "flex-start",
              flexDirection: "column",
              "&:hover": {
                backgroundColor: colors.blue20,
              },
            }}
          >
            <Text fontWeight={600}>{deal.name}</Text>
            <Text variant="text3">{deal.company.name}</Text>
          </ButtonBase>
        ))}
      </Stack>

      <Button
        sx={{ alignSelf: "flex-end", flexShrink: 0 }}
        disabled={_.isNil(selectedDeal)}
        onClick={
          _.isNil(selectedDeal)
            ? undefined
            : () => onStepAdvance({ deal: selectedDeal })
        }
      >
        Next
      </Button>
    </Stack>
  );
};

export default DealSelect;
